import { useState } from "react";
import { Check, X, Loader2, Trash2, Mail, Phone, MapPin, ExternalLink, ArrowRight, Archive } from "lucide-react";
import { toast } from "sonner";
import { useNavigate } from "react-router-dom";
import { Modal } from "@/components/common";
import { api } from "@/lib/api";
import AbschlussDialog from "@/components/AbschlussDialog";
import MailAcceptDuplicateDialog from "@/components/MailAcceptDuplicateDialog";
import { useProjektErstellenFlow } from "@/components/ProjektErstellenDialog";
import AuswahlFeld, { useStichwortVorschlag, KUNDEN_STATUS_ERSATZ } from "@/components/AuswahlFeld";
import { hilfeOeffnen } from "@/lib/useF1Help";

/**
 * MailDetailModal
 * Öffnet eine Mail-Anfrage zum Prüfen.
 * Zeigt:
 *  - Volltext der Mail (body_excerpt)
 *  - Geparste Felder (Anrede, Name, E-Mail, Telefon, Adresse, Anliegen)
 *  - Aktionen:
 *      • Als Kunde übernehmen → legt Kunde mit den geparsten Daten an
 *        und navigiert direkt ins Kunden-Modul (Bearbeiten-Maske offen)
 *      • Ignorieren
 *      • Löschen
 *      • Schließen
 *
 * Folgt VISION.md: KEIN eigenes Kunden-Formular hier — Bearbeitung passiert
 * im bestehenden Kunden-Modul (Datenmaske, einzige Wahrheit).
 */
const MailDetailModal = ({ entry, onClose, onChanged, onKategorien, onKundenStatus }) => {
  const navigate = useNavigate();
  const [busy, setBusy] = useState("");
  const [showAbschluss, setShowAbschluss] = useState(false);
  const [duplicates, setDuplicates] = useState(null); // null | array
  // Paket 3 (25.09.2026): Anfrage korrigieren + Zuordnung lösen
  const [bearbeitenId, setBearbeitenId] = useState(null);
  const [formular, setFormular] = useState({});
  const [lokalParsed, setLokalParsed] = useState(null);
  const [vorschau, setVorschau] = useState(null);
  const [auswahl, setAuswahl] = useState({ kunde: true, email: true, projekte: {} });

  // A-010 M3d: Kategorien – sofort an der Anfrage gespeichert
  const [katStand, setKatStand] = useState(null); // { id, liste, status: "" | "speichert" | "gespeichert" }
  // A-010 M3e: Kunden-Status – sofort an der Anfrage (bzw. beim zugeordneten Kunden) gespeichert
  const [ksStand, setKsStand] = useState(null); // { id, wert, status: "" | "speichert" | "gespeichert", kunde }
  const [ksAbschluss, setKsAbschluss] = useState(null); // Status, der noch einen Abschluss-Grund braucht

  const { askAndCreate: askProjektConfirm, dialog: projektDialogElement } = useProjektErstellenFlow();
  // A-010 M3e: Kategorie-Vorschlag aus Anliegen + Betreff (Hook vor dem frühen return)
  const vorschlagText = entry ? `${((lokalParsed && lokalParsed.id === entry.id ? lokalParsed.parsed : null) || entry.parsed || {}).nachricht || ""} ${entry.subject || ""}` : "";
  const katVorschlag = useStichwortVorschlag(vorschlagText, "kunden_kategorie", "projekt_kategorie");
  if (!entry) return null;
  const kategorien = katStand && katStand.id === entry.id ? katStand.liste : (entry.kategorien || []);
  const kategorienSetzen = async (liste) => {
    const vorher = kategorien;
    setKatStand({ id: entry.id, liste, status: "speichert" });
    try {
      const r = await api.patch(`/module-mail-inbox/${entry.id}/kategorien`, { kategorien: liste });
      setKatStand({ id: entry.id, liste: r.data.kategorien, status: "gespeichert" });
      onKategorien?.(entry.id, r.data.kategorien);
      if (r.data.beim_kunden_ergaenzt) toast.success("Kategorie auch beim zugeordneten Kunden ergänzt");
    } catch (err) {
      setKatStand({ id: entry.id, liste: vorher, status: "" });
      toast.error(err?.response?.data?.detail || "Kategorie nicht gespeichert");
    }
  };
  const kundeZugeordnet = entry.status === "übernommen" && !!entry.kunde_id;
  const kundenStatus = ksStand && ksStand.id === entry.id
    ? ksStand.wert
    : ((kundeZugeordnet && entry.kunde_status_live) || entry.kunden_status || "Anfrage");
  const kundenStatusSpeichern = async (wert, grund = "") => {
    const vorher = kundenStatus;
    setKsStand({ id: entry.id, wert, status: "speichert" });
    try {
      const r = await api.patch(`/module-mail-inbox/${entry.id}/kunden-status`, { kunden_status: wert, abschluss_grund: grund });
      setKsStand({ id: entry.id, wert: r.data.kunden_status, status: "gespeichert", kunde: r.data.beim_kunden_gesetzt ? (r.data.kunde_name || "Kunde") : "" });
      onKundenStatus?.(entry.id, r.data.kunden_status, r.data.beim_kunden_gesetzt);
      if (r.data.beim_kunden_gesetzt) {
        toast.success(`Kunden-Status „${r.data.kunden_status}“ beim Kunden${r.data.kunde_name ? ` ${r.data.kunde_name}` : ""} gesetzt`);
        try { window.dispatchEvent(new CustomEvent("graupner:data-changed")); } catch { /* noop */ }
      }
    } catch (err) {
      setKsStand({ id: entry.id, wert: vorher, status: "" });
      toast.error(err?.response?.data?.detail || "Kunden-Status nicht gespeichert");
    }
  };
  const kundenStatusWaehlen = (wert) => {
    // wie „Kunde bearbeiten“: Abgeschlossen/Archiv beim Kunden nur mit Abschluss-Grund
    if (kundeZugeordnet && ["Abgeschlossen", "Archiv"].includes(wert) && !["Abgeschlossen", "Archiv"].includes(kundenStatus)) {
      setKsAbschluss(wert);
      return;
    }
    kundenStatusSpeichern(wert);
  };
  const p = (lokalParsed && lokalParsed.id === entry.id ? lokalParsed.parsed : null) || entry.parsed || {};
  const fullName = [p.vorname, p.nachname].filter(Boolean).join(" ") || entry.from_name || "(ohne Name)";
  const isVorschlag = entry.status === "vorschlag" || entry.status === "spam_verdacht";

  const abschliessen = async ({ grund }) => {
    try {
      await api.post(`/module-mail-inbox/${entry.id}/abschliessen`, { grund });
      toast.success("Anfrage abgeschlossen – im Archiv sichtbar");
      setShowAbschluss(false);
      onChanged?.();
      onClose?.();
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Abschließen fehlgeschlagen");
    }
  };


  const accept = async (forceNew = false) => {
    setBusy("accept");
    try {
      const r = await api.post(
        `/module-mail-inbox/accept/${entry.id}`,
        forceNew ? { force_new: true } : {},
      );
      toast.success(`Kunde „${r.data.kunde_name || fullName}" angelegt — öffne Datenmaske…`);
      onChanged?.();
      try { window.dispatchEvent(new CustomEvent("graupner:data-changed")); } catch { /* noop */ }
      setDuplicates(null);
      await askProjektConfirm(r.data.kunde_id, { mailId: entry.id });
      navigate(`/module/kunden?edit=${r.data.kunde_id}`);
    } catch (err) {
      const detail = err?.response?.data?.detail;
      if (err?.response?.status === 409 && detail && detail.code === "duplicate_kunde") {
        setDuplicates(detail.duplicates || []);
        setBusy("");
        return;
      }
      toast.error(typeof detail === "string" ? detail : "Übernahme fehlgeschlagen");
      setBusy("");
    }
  };

  const linkToExisting = async (kundeId) => {
    try {
      const r = await api.post(`/module-mail-inbox/accept-link/${entry.id}`, {
        kunde_id: kundeId,
        append_nachricht: true,
      });
      toast.success(`Anfrage zu „${r.data.kunde_name}" zugeordnet`);
      onChanged?.();
      try { window.dispatchEvent(new CustomEvent("graupner:data-changed")); } catch { /* noop */ }
      setDuplicates(null);
      onClose?.();
      navigate(`/module/kunden?edit=${kundeId}`);
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Zuordnen fehlgeschlagen");
    }
  };

  // ── Paket 3 (25.09.2026): Anfrage korrigieren + Zuordnung lösen ──
  const FELDER = [["anrede", "Anrede"], ["vorname", "Vorname"], ["nachname", "Nachname"], ["email", "E-Mail"], ["telefon", "Telefon"], ["strasse", "Straße"], ["plz", "PLZ"], ["ort", "Ort"]];

  const bearbeitenStarten = () => {
    const f = {};
    FELDER.forEach(([k]) => { f[k] = p[k] || ""; });
    f.nachricht = p.nachricht || "";
    setFormular(f);
    setBearbeitenId(entry.id);
  };

  const angabenSpeichern = async () => {
    setBusy("angaben");
    try {
      const r = await api.patch(`/module-mail-inbox/${entry.id}/angaben`, formular);
      setLokalParsed({ id: entry.id, parsed: r.data.parsed || { ...p, ...formular } });
      toast.success(r.data.geaendert ? `${r.data.geaendert} Angabe(n) korrigiert` : "Keine Änderung");
      setBearbeitenId(null);
      onChanged?.();
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Speichern fehlgeschlagen");
    } finally {
      setBusy("");
    }
  };

  const zuordnungVorschauLaden = async () => {
    setBusy("vorschau");
    try {
      const r = await api.get(`/module-mail-inbox/${entry.id}/zuordnung-vorschau`);
      const pr = {};
      (r.data.projekte || []).forEach((x) => { pr[x.id] = true; });
      setAuswahl({ kunde: true, email: true, projekte: pr });
      setVorschau({ id: entry.id, ...r.data });
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Vorschau fehlgeschlagen");
    } finally {
      setBusy("");
    }
  };

  const zuordnungLoesen = async () => {
    const name = vorschau?.kunde?.name || "diesem Kunden";
    if (!window.confirm(`Zuordnung zu „${name}" wirklich lösen? Die Anfrage ist danach wieder offen.`)) return;
    setBusy("loesen");
    try {
      await api.post(`/module-mail-inbox/${entry.id}/zuordnung-loesen`, {
        kunde_text_entfernen: !!auswahl.kunde,
        kunde_email_leeren: !!auswahl.email,
        projekt_ids: Object.keys(auswahl.projekte).filter((k) => auswahl.projekte[k]),
      });
      toast.success("Zuordnung gelöst – Anfrage ist wieder offen");
      setVorschau(null);
      onChanged?.();
      try { window.dispatchEvent(new CustomEvent("graupner:data-changed")); } catch { /* noop */ }
      onClose?.();
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Lösen fehlgeschlagen");
    } finally {
      setBusy("");
    }
  };

  // ── Nachtrag 3b (25.09.2026): Hinweise auf fehlende oder fragwürdige Angaben ──
  // Die Suite warnt nur – entscheiden tut der Mensch.
  const PLATZHALTER = ["strasse", "straße", "str", "str.", "ort", "plz", "name", "vorname", "nachname", "-", "x", "."];
  const leer = (v) => !v || !String(v).trim() || PLATZHALTER.includes(String(v).trim().toLowerCase());
  const hinweise = [];
  const hMail = (p.email || "").trim();
  if (!hMail) hinweise.push("Keine E-Mail-Adresse angegeben");
  else if (!/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(hMail)) hinweise.push("E-Mail-Adresse ungültig");
  if (leer(p.nachname)) hinweise.push("Nachname fehlt");
  if (leer(p.vorname)) hinweise.push("Vorname fehlt");
  if (!hMail && leer(p.telefon)) hinweise.push("Weder E-Mail noch Telefon – Kunde nicht erreichbar");
  const hPlz = (p.plz || "").trim();
  if (leer(p.strasse) || leer(p.ort) || !hPlz) hinweise.push("Adresse unvollständig");
  else if (!/^\d{5}$/.test(hPlz)) hinweise.push("PLZ ist nicht fünfstellig");

  const reject = async () => {
    if (!window.confirm("Diese Anfrage als ignoriert markieren?")) return;
    setBusy("reject");
    try {
      await api.post(`/module-mail-inbox/reject/${entry.id}`);
      toast.success("Anfrage ignoriert");
      onChanged?.();
      onClose?.();
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Fehler");
    } finally {
      setBusy("");
    }
  };

  const remove = async () => {
    if (!window.confirm("Diesen Eintrag endgültig löschen? Bei späteren Scans wird er nicht erneut importiert.")) return;
    setBusy("delete");
    try {
      await api.delete(`/module-mail-inbox/${entry.id}`);
      toast.success("Eintrag gelöscht");
      onChanged?.();
      onClose?.();
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Löschen fehlgeschlagen");
    } finally {
      setBusy("");
    }
  };

  return (
    <Modal isOpen={!!entry} onClose={onClose} title="Mail-Anfrage prüfen" size="xl">
      {projektDialogElement}
      <div className="space-y-3 text-sm" data-testid="mail-detail-modal">
        {/* Kopf */}
        <div className="border rounded-sm p-3 bg-muted/30">
          <div className="flex flex-wrap items-center gap-2">
            <h3 className="font-semibold text-base">
              {p.anrede ? `${p.anrede} ` : ""}{fullName}
            </h3>
            {p.format && (
              <span className="text-[10px] px-1.5 py-0.5 rounded-sm bg-slate-100 text-slate-600 font-mono">{p.format}</span>
            )}
            {entry.account_label && (
              <span className="text-[10px] px-1.5 py-0.5 rounded-sm bg-blue-50 text-blue-700">{entry.account_label}</span>
            )}
          </div>
          <div className="text-xs text-muted-foreground mt-1 break-words">
            <span className="font-mono">Betreff:</span> {entry.subject || "(kein Betreff)"}
          </div>
          <div className="text-xs text-muted-foreground break-all">
            <span className="font-mono">Von:</span> {entry.from_email || "—"}
            {entry.received_at && <> · <span className="font-mono">{new Date(entry.received_at).toLocaleString("de-DE")}</span></>}
          </div>
        </div>

        {/* Geparste Felder */}
        <div className="border rounded-sm p-3 space-y-1">
          {hinweise.length > 0 && bearbeitenId !== entry.id && (
            <div className="border border-amber-300 bg-amber-50 text-amber-900 dark:bg-amber-950/40 dark:text-amber-200 rounded-sm p-2 mb-2 text-xs" data-testid="anfrage-hinweise">
              <div className="flex items-start justify-between gap-2">
                <ul className="list-disc pl-4 space-y-0.5">
                  {hinweise.map((h) => <li key={h}>{h}</li>)}
                </ul>
                <button onClick={bearbeitenStarten} disabled={!!busy} className="shrink-0 px-2 py-1 border border-amber-400 rounded-sm hover:bg-amber-100 dark:hover:bg-amber-900/40" data-testid="btn-hinweise-korrigieren">
                  Jetzt korrigieren
                </button>
              </div>
            </div>
          )}
          <div className="flex items-center justify-between gap-2 mb-1">
            <div className="text-xs font-semibold text-muted-foreground">Erkannte Daten (werden als Kunde übernommen)</div>
            {bearbeitenId !== entry.id && (
              <button onClick={bearbeitenStarten} disabled={!!busy} className="text-xs px-2 py-1 border border-border bg-muted/40 text-foreground rounded-sm hover:bg-muted disabled:opacity-50" data-testid="btn-angaben-korrigieren">
                Angaben korrigieren
              </button>
            )}
          </div>
          {bearbeitenId === entry.id && (
            <div className="border rounded-sm p-2 mb-2 bg-muted/30 space-y-2" data-testid="angaben-formular">
              <div className="text-xs text-muted-foreground">Korrigiert nur diese Anfrage – eine bereits bestehende Kundenkarte bleibt unverändert.</div>
              <div className="grid grid-cols-1 sm:grid-cols-2 gap-2">
                {FELDER.map(([k, label]) => (
                  <label key={k} className="text-xs">
                    <span className="block text-muted-foreground mb-0.5">{label}</span>
                    <input
                      value={formular[k] || ""}
                      onChange={(ev) => setFormular({ ...formular, [k]: ev.target.value })}
                      className="w-full border border-border rounded-sm px-2 py-1 text-sm bg-background text-foreground"
                    />
                  </label>
                ))}
              </div>
              <label className="text-xs block">
                <span className="block text-muted-foreground mb-0.5">Anliegen</span>
                <textarea
                  rows={3}
                  value={formular.nachricht || ""}
                  onChange={(ev) => setFormular({ ...formular, nachricht: ev.target.value })}
                  className="w-full border border-border rounded-sm px-2 py-1 text-sm bg-background text-foreground"
                />
              </label>
              <div className="flex justify-end gap-2">
                <button onClick={() => setBearbeitenId(null)} disabled={!!busy} className="px-3 py-1.5 text-sm border border-border bg-muted/40 text-foreground rounded-sm hover:bg-muted disabled:opacity-50">Abbrechen</button>
                <button onClick={angabenSpeichern} disabled={!!busy} className="px-3 py-1.5 text-sm bg-emerald-600 text-white rounded-sm hover:bg-emerald-700 inline-flex items-center gap-1 disabled:opacity-50" data-testid="btn-angaben-speichern">
                  {busy === "angaben" ? <Loader2 className="w-4 h-4 animate-spin" /> : <Check className="w-4 h-4" />}
                  Speichern
                </button>
              </div>
            </div>
          )}
          <div className="grid grid-cols-1 sm:grid-cols-2 gap-x-4 gap-y-1 text-sm">
            {p.email && (
              <div className="flex items-center gap-2 min-w-0">
                <Mail className="w-3.5 h-3.5 text-muted-foreground flex-shrink-0" />
                <a href={`mailto:${p.email}`} className="text-primary hover:underline truncate">{p.email}</a>
              </div>
            )}
            {p.telefon && (
              <div className="flex items-center gap-2 min-w-0">
                <Phone className="w-3.5 h-3.5 text-muted-foreground flex-shrink-0" />
                <a href={`tel:${p.telefon.replace(/\s/g, "")}`} className="text-primary hover:underline truncate">{p.telefon}</a>
              </div>
            )}
            {(p.strasse || p.plz || p.ort) && (
              <div className="flex items-center gap-2 sm:col-span-2 min-w-0">
                <MapPin className="w-3.5 h-3.5 text-muted-foreground flex-shrink-0" />
                <span className="truncate">
                  {[p.strasse, [p.plz, p.ort].filter(Boolean).join(" ")].filter(Boolean).join(", ")}
                </span>
              </div>
            )}
            {p.source_url && (
              <div className="flex items-center gap-2 sm:col-span-2 min-w-0">
                <ExternalLink className="w-3.5 h-3.5 text-muted-foreground flex-shrink-0" />
                <a href={p.source_url} target="_blank" rel="noopener noreferrer" className="text-xs text-primary hover:underline truncate">{p.source_url}</a>
              </div>
            )}
          </div>
          {p.nachricht && (
            <div className="pt-2 border-t mt-2">
              <div className="text-xs font-semibold text-muted-foreground mb-1">Anliegen</div>
              <pre className="text-xs whitespace-pre-wrap break-words">{p.nachricht}</pre>
            </div>
          )}
          {/* A-010 M3e: Kategorie als Auswahlliste (statt M3d-Chips) direkt unter dem Anliegen, sofort gespeichert */}
          <div className="pt-2 border-t mt-2" data-testid="detail-kategorien">
            <AuswahlFeld
              docType="kunden_kategorie"
              label="Kategorie"
              value={kategorien[0] || ""}
              onChange={(k) => kategorienSetzen(k ? [k] : [])}
              leer="— keine Kategorie —"
              vorschlag={katVorschlag}
              busy={!!(katStand && katStand.id === entry.id && katStand.status === "speichert")}
              testId="anfrage-kategorie"
              hinweis={katStand && katStand.id === entry.id && katStand.status ? (
                <div className="text-[11px] text-muted-foreground mt-1" data-testid="kategorien-status">
                  {katStand.status === "speichert" ? "speichert …" : "an der Anfrage gespeichert ✓"}
                </div>
              ) : null}
            />
            {/* A-014: Hilfe zu Stichwörtern per Klick */}
            <button type="button" onClick={() => hilfeOeffnen("hilfe_kategorien_stichwoerter")}
              className="mt-1 text-[11px] text-primary hover:underline py-1" data-testid="anfrage-kategorie-hilfe">
              Wie entsteht der Vorschlag?
            </button>
          </div>
          {/* A-010 M3e: Kunden-Status direkt unter den Kategorien, sofort gespeichert */}
          <div className="pt-2 border-t mt-2" data-testid="detail-kunden-status">
            <AuswahlFeld
              docType="kunden_status"
              label="Kunden-Status"
              value={kundenStatus}
              onChange={kundenStatusWaehlen}
              ersatz={KUNDEN_STATUS_ERSATZ}
              stichwoerter={false}
              testId="anfrage-kunden-status"
              busy={!!(ksStand && ksStand.id === entry.id && ksStand.status === "speichert")}
              hinweis={
                <div className="text-[11px] text-muted-foreground mt-1" data-testid="kunden-status-hinweis">
                  {ksStand && ksStand.id === entry.id && ksStand.status === "speichert"
                    ? "speichert …"
                    : ksStand && ksStand.id === entry.id && ksStand.status === "gespeichert"
                      ? (ksStand.kunde ? `beim Kunden ${ksStand.kunde} gespeichert ✓` : "an der Anfrage gespeichert ✓ – geht beim Übernehmen an den Kunden")
                      : kundeZugeordnet
                        ? "Anfrage ist einem Kunden zugeordnet: eine Änderung setzt den Status beim Kunden."
                        : "Geht beim Übernehmen an den neuen bzw. verknüpften Kunden. (Nicht der Bearbeitungsstand der Anfrage.)"}
                </div>
              }
            />
          </div>
        </div>

        {/* Paket 3 (25.09.2026): Zuordnung prüfen / lösen */}
        {entry.status === "übernommen" && entry.kunde_id && (
          <div className="border rounded-sm p-3 space-y-2" data-testid="zuordnung-bereich">
            <div className="flex items-center justify-between gap-2">
              <div className="text-xs font-semibold text-muted-foreground">
                Zugeordnet zu: {vorschau && vorschau.id === entry.id && vorschau.kunde ? vorschau.kunde.name : "einer Kundenkarte"}
              </div>
              {!(vorschau && vorschau.id === entry.id) && (
                <button onClick={zuordnungVorschauLaden} disabled={!!busy} className="text-xs px-2 py-1 border border-border bg-muted/40 text-foreground rounded-sm hover:bg-muted disabled:opacity-50" data-testid="btn-zuordnung-pruefen">
                  {busy === "vorschau" ? "Lade…" : "Zuordnung prüfen / lösen"}
                </button>
              )}
            </div>
            {vorschau && vorschau.id === entry.id && (
              <div className="space-y-2 text-xs">
                {vorschau.neu_angelegt && (
                  <div className="text-muted-foreground">Diese Kundenkarte wurde aus dieser Anfrage angelegt. Sie bleibt bestehen – bei Bedarf im Kunden-Modul selbst löschen.</div>
                )}
                {vorschau.kunde_block ? (
                  <label className="flex items-start gap-2">
                    <input type="checkbox" className="mt-0.5" checked={!!auswahl.kunde} onChange={(ev) => setAuswahl({ ...auswahl, kunde: ev.target.checked })} />
                    <span className="min-w-0 flex-1">
                      Diesen angehängten Text in der Kundenkarte „{vorschau.kunde?.name}" entfernen:
                      <pre className="mt-1 p-2 border border-border rounded-sm whitespace-pre-wrap break-words bg-muted/30">{vorschau.kunde_block}</pre>
                    </span>
                  </label>
                ) : (
                  <div className="text-muted-foreground">In der Kundenkarte wurde kein angehängter Text dieser Anfrage gefunden.</div>
                )}
                {(vorschau.projekte || []).map((pr) => (
                  <label key={pr.id} className="flex items-start gap-2">
                    <input
                      type="checkbox"
                      className="mt-0.5"
                      checked={!!auswahl.projekte[pr.id]}
                      onChange={(ev) => setAuswahl({ ...auswahl, projekte: { ...auswahl.projekte, [pr.id]: ev.target.checked } })}
                    />
                    <span className="min-w-0 flex-1">
                      Diesen Text im Projekt „{pr.titel || "ohne Titel"}" entfernen:
                      <pre className="mt-1 p-2 border border-border rounded-sm whitespace-pre-wrap break-words bg-muted/30">{pr.block}</pre>
                    </span>
                  </label>
                ))}
                {vorschau.email_gesperrt && (
                  <label className="flex items-start gap-2">
                    <input type="checkbox" className="mt-0.5" checked={!!auswahl.email} onChange={(ev) => setAuswahl({ ...auswahl, email: ev.target.checked })} />
                    <span>Gesperrte E-Mail-Adresse „{vorschau.kunde?.email}" in dieser Kundenkarte löschen</span>
                  </label>
                )}
                <div className="flex justify-end gap-2 pt-1">
                  <button onClick={() => setVorschau(null)} disabled={!!busy} className="px-3 py-1.5 text-sm border border-border bg-muted/40 text-foreground rounded-sm hover:bg-muted disabled:opacity-50">Abbrechen</button>
                  <button onClick={zuordnungLoesen} disabled={!!busy} className="px-3 py-1.5 text-sm bg-red-600 text-white rounded-sm hover:bg-red-700 disabled:opacity-50" data-testid="btn-zuordnung-loesen">
                    {busy === "loesen" ? "Löse…" : "Zuordnung jetzt lösen"}
                  </button>
                </div>
              </div>
            )}
          </div>
        )}

        {/* Volltext der Mail */}
        <div className="border rounded-sm">
          <div className="text-xs font-semibold text-muted-foreground px-3 py-2 border-b bg-muted/20">
            Mail-Volltext
          </div>
          <pre
            className="text-xs whitespace-pre-wrap break-words p-3 max-h-[40vh] overflow-auto"
            data-testid="mail-detail-body"
          >
            {entry.body_excerpt || "(kein Inhalt vorhanden)"}
          </pre>
        </div>

        {/* Aktionen */}
        <div className="flex flex-wrap justify-between gap-2 pt-2 border-t">
          <button
            onClick={onClose}
            className="px-3 py-2 text-sm border rounded-sm hover:bg-muted"
            disabled={!!busy}
            data-testid="btn-detail-close"
          >
            Schließen
          </button>
          {isVorschlag && (
            <div className="flex gap-2 flex-wrap">
              <button
                onClick={remove}
                disabled={!!busy}
                className="px-3 py-2 text-sm border border-red-200 text-red-700 rounded-sm hover:bg-red-50 inline-flex items-center gap-1 disabled:opacity-50"
                data-testid="btn-detail-delete"
              >
                {busy === "delete" ? <Loader2 className="w-4 h-4 animate-spin" /> : <Trash2 className="w-4 h-4" />}
                Löschen
              </button>
              <button
                onClick={reject}
                disabled={!!busy}
                className="px-3 py-2 text-sm border rounded-sm hover:bg-muted inline-flex items-center gap-1 disabled:opacity-50"
                data-testid="btn-detail-reject"
              >
                {busy === "reject" ? <Loader2 className="w-4 h-4 animate-spin" /> : <X className="w-4 h-4" />}
                Ignorieren
              </button>
              <button
                onClick={() => setShowAbschluss(true)}
                disabled={!!busy}
                className="px-3 py-2 text-sm border border-slate-300 text-slate-700 rounded-sm hover:bg-slate-50 inline-flex items-center gap-1 disabled:opacity-50"
                data-testid="btn-detail-abschliessen"
                title="Mit Grund abschließen – bleibt im Archiv sichtbar"
              >
                <Archive className="w-4 h-4" />
                Abschließen
              </button>
              <button
                onClick={() => {
                  if (hinweise.length && !window.confirm(`Achtung – bei dieser Anfrage fehlt etwas oder stimmt nicht:\n• ${hinweise.join("\n• ")}\n\nTrotzdem als Kunde übernehmen?`)) return;
                  accept(false);
                }}
                disabled={!!busy}
                className="px-4 py-2 text-sm bg-emerald-600 text-white rounded-sm hover:bg-emerald-700 inline-flex items-center gap-1 disabled:opacity-50"
                data-testid="btn-detail-accept"
                title="Anlegen und im Kunden-Modul öffnen"
              >
                {busy === "accept" ? <Loader2 className="w-4 h-4 animate-spin" /> : <Check className="w-4 h-4" />}
                Als Kunde übernehmen
                <ArrowRight className="w-3.5 h-3.5" />
              </button>
            </div>
          )}
        </div>
      </div>

      <AbschlussDialog
        isOpen={showAbschluss}
        onClose={() => setShowAbschluss(false)}
        onConfirm={abschliessen}
        subjectLabel={`${fullName}${entry.subject ? " – " + entry.subject : ""}`}
      />

      <AbschlussDialog
        isOpen={!!ksAbschluss}
        onClose={() => setKsAbschluss(null)}
        onConfirm={async ({ grund }) => { const wert = ksAbschluss; setKsAbschluss(null); await kundenStatusSpeichern(wert, grund); }}
        titleLabel={`Kunden-Status „${ksAbschluss || ""}“`}
        subjectLabel={fullName}
      />

      <MailAcceptDuplicateDialog
        open={Array.isArray(duplicates)}
        duplicates={duplicates || []}
        onLink={linkToExisting}
        onForce={() => accept(true)}
        onClose={() => setDuplicates(null)}
      />
    </Modal>
  );
};

export default MailDetailModal;
